/**
 * React hooks for analytics
 */

import { useCallback, useEffect } from 'react';
import { analytics } from './index';
import { 
  anonymizeAmount, 
  sanitizePayee 
} from './privacy';
import type { 
  UserTraits 
} from './types';

/**
 * Main analytics hook
 */
export function useAnalytics() {
  const track = useCallback(async (event: string, properties?: Record<string, any>) => {
    await analytics.track(event, properties);
  }, []);

  const identify = useCallback(async (userId: string, traits?: UserTraits) => {
    await analytics.identify(userId, traits);
  }, []);

  const page = useCallback(async (name?: string, properties?: Record<string, any>) => {
    await analytics.page(name, properties);
  }, []);

  const error = useCallback(async (error: Error | string, context?: Record<string, any>) => {
    await analytics.error(error, context);
  }, []);

  return { track, identify, page, error };
}

/**
 * Hook for tracking financial events with automatic sanitization
 */
export function useFinancialAnalytics() {
  const { track } = useAnalytics();

  const trackBudgetCreated = useCallback(async (data: {
    budgetName: string;
    categoryCount: number;
    totalAmount: number;
    currency: string;
  }) => {
    await track('budget_created', {
      category: 'budgets',
      feature_area: 'budget_management',
      action_type: 'create',
      amount_range: anonymizeAmount(data.totalAmount),
      currency: data.currency,
      metadata: {
        category_count: data.categoryCount,
      },
    });
  }, [track]);

  const trackBudgetUpdated = useCallback(async (data: {
    budgetId: string;
    changeType: 'amount' | 'category' | 'name';
    oldAmount?: number;
    newAmount?: number;
  }) => {
    await track('budget_updated', {
      category: 'budgets',
      feature_area: 'budget_management',
      action_type: 'update',
      metadata: {
        change_type: data.changeType,
        amount_changed: data.oldAmount && data.newAmount ? 
          anonymizeAmount(Math.abs(data.newAmount - data.oldAmount)) : undefined,
      },
    });
  }, [track]);

  const trackTransactionAdded = useCallback(async (data: {
    amount: number;
    category: string;
    payee?: string;
    description?: string;
    isRecurring?: boolean;
  }) => {
    await track('transaction_added', {
      category: 'transactions',
      feature_area: 'transaction_management',
      action_type: 'create',
      amount_range: anonymizeAmount(data.amount),
      metadata: {
        category: data.category,
        payee_type: data.payee ? sanitizePayee(data.payee) : 'unknown',
        has_description: !!data.description,
        is_recurring: data.isRecurring || false,
      },
    });
  }, [track]);

  const trackTransactionCategorized = useCallback(async (data: {
    transactionCount: number;
    fromCategory?: string;
    toCategory: string;
    isBulk: boolean;
  }) => {
    await track('transaction_categorized', {
      category: 'transactions',
      feature_area: 'transaction_management',
      action_type: 'categorize',
      metadata: {
        transaction_count: data.transactionCount,
        from_category: data.fromCategory || 'uncategorized',
        to_category: data.toCategory,
        is_bulk: data.isBulk,
      },
    });
  }, [track]);

  const trackAccountConnected = useCallback(async (data: {
    accountType: string;
    connectionMethod: 'manual' | 'plaid' | 'other';
    initialBalance?: number;
  }) => {
    await track('account_connected', {
      category: 'accounts',
      feature_area: 'account_management',
      action_type: 'connect',
      metadata: {
        account_type: data.accountType,
        connection_method: data.connectionMethod,
        has_initial_balance: data.initialBalance !== undefined,
      },
    });
  }, [track]);

  const trackGoalCreated = useCallback(async (data: {
    goalType: string;
    targetAmount: number;
    targetDate?: Date;
    currency: string;
  }) => {
    await track('goal_created', {
      category: 'goals',
      feature_area: 'goal_tracking',
      action_type: 'create',
      amount_range: anonymizeAmount(data.targetAmount),
      currency: data.currency,
      metadata: {
        goal_type: data.goalType,
        has_target_date: !!data.targetDate,
        days_to_target: data.targetDate ? 
          Math.floor((data.targetDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 
          undefined,
      },
    });
  }, [track]);

  const trackBudgetOverspend = useCallback(async (data: {
    categoryName: string;
    budgetAmount: number;
    actualAmount: number;
    percentOver: number;
  }) => {
    await track('budget_overspend_alert', {
      category: 'budgets',
      feature_area: 'budget_alerts',
      action_type: 'alert',
      metadata: {
        category: data.categoryName,
        budget_range: anonymizeAmount(data.budgetAmount),
        actual_range: anonymizeAmount(data.actualAmount),
        percent_over: Math.round(data.percentOver),
      },
    });
  }, [track]);

  return {
    trackBudgetCreated,
    trackBudgetUpdated,
    trackTransactionAdded,
    trackTransactionCategorized,
    trackAccountConnected,
    trackGoalCreated,
    trackBudgetOverspend,
  };
}

/**
 * Hook for tracking conversion events
 */
export function useConversionAnalytics() {
  const { track } = useAnalytics();

  const trackTrialStarted = useCallback(async (plan: string) => {
    await track('trial_started', {
      category: 'conversion',
      type: 'trial_start',
      plan,
    });
  }, [track]);

  const trackSubscriptionEvent = useCallback(async (data: {
    type: 'upgrade' | 'downgrade' | 'cancel' | 'reactivate';
    fromPlan?: string;
    toPlan?: string;
    value?: number;
    currency?: string;
  }) => {
    await track(`subscription_${data.type}`, {
      category: 'conversion',
      type: 'subscription',
      plan: data.toPlan,
      value: data.value,
      currency: data.currency,
      metadata: {
        from_plan: data.fromPlan,
        event_type: data.type,
      },
    });
  }, [track]);

  const trackFeatureAdoption = useCallback(async (feature: string) => {
    await track('feature_adopted', {
      category: 'conversion',
      type: 'feature_adoption',
      metadata: {
        feature_name: feature,
      },
    });
  }, [track]);

  return {
    trackTrialStarted,
    trackSubscriptionEvent,
    trackFeatureAdoption,
  };
}

/**
 * Hook to track component visibility
 */
export function useTrackVisibility(componentName: string, properties?: Record<string, any>) {
  const { track } = useAnalytics();

  useEffect(() => {
    const startTime = Date.now();

    // Track component view
    track('component_viewed', {
      component: componentName,
      ...properties,
    });

    // Track time spent on unmount
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      track('component_time_spent', {
        component: componentName,
        duration_seconds: timeSpent,
        ...properties,
      });
    };
  }, [componentName, track]);
}

/**
 * Hook to track user preferences
 */
export function usePreferenceAnalytics() {
  const { track } = useAnalytics();

  const trackPreferenceChanged = useCallback(async (data: {
    preference: string;
    oldValue: any;
    newValue: any;
  }) => {
    await track('preference_changed', {
      preference_name: data.preference,
      old_value: String(data.oldValue),
      new_value: String(data.newValue),
    });
  }, [track]);

  return { trackPreferenceChanged };
}